import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import MediaCardContent from './MediaCardContent'
import { CancelledIcon, ExpandIcon } from 'images'
import { fileStatus } from 'components/Recommendations/Campaign/Media/media'
import { useTranslation } from 'react-i18next'
import MediaLabel from './MediaLabel'
import CustomCheckBox from 'components/Shared/CustomCheckBox'

const MediaPreview = ({ file }) => {
  const src = file.preview || file.url

  if (file.type?.startsWith('video')) {
    return <video className="media-list-item__preview-media" controls={false} src={src} title={file.name || file.filename} />
  }

  return <img className="media-list-item__preview-media" src={src} alt={file.name || file.filename} />
}

export default function MediaCard(props) {
  const {
    item,
    selectiveMode = false,
    selectedMedia = [],
    setSelectedMedia = () => {},
    onPreviewClick,
    onExpandClick,
    onRemove,
    showLabel = false,
    mediaLabel,
    actionMenuItems = [],
    onVariantClick,
    isGroupHighlighted = false,
    isDimmed = false,
    onDimmedClick,
    onClick,
    disableSelection = false,
  } = props
  const isError = item.status === fileStatus.uploadFailed
  const isSelected = selectedMedia.includes(item.id)
  const { t } = useTranslation()

  const handleToggleSelection = () => {
    if (disableSelection) {
      return
    }

    setSelectedMedia((prev) =>
      prev.includes(item.id)
        ? prev.filter((id) => id !== item.id)
        : [...prev, item.id],
    )
  }

  const handlePreviewClick = () => {
    onPreviewClick?.(item)
  }

  const handleExpandClick = (event) => {
    event.stopPropagation()
    onExpandClick?.(item)
  }

  const expandButton = onExpandClick ? (
    <button type="button" className="expand-button" onClick={handleExpandClick}>
      <span className="icon">
        <ExpandIcon width={16} height={16} />
      </span>
    </button>
  ) : null

  return (
    <div
      className={classnames('media-list-item', {
        'selected': isSelected,
        'selective-mode-item': selectiveMode,
        'media-list-item--group-highlighted': isGroupHighlighted,
        'media-list-item--interactive': Boolean(onClick),
      })}
      onClick={onClick ? () => onClick(item) : undefined}
    >
      <div
        className={classnames('media-list-item__preview', {
          'media-list-item__preview--clickable': !selectiveMode && onPreviewClick,
        })}
        onClick={!selectiveMode && onPreviewClick ? handlePreviewClick : undefined}
      >
        <MediaPreview file={item} />

        {isError && <div className="error-overlay">
          <div className="icon">
            <CancelledIcon width={12} height={12} />
          </div>
          <p className="title">{t('common.genericError')}</p>
        </div>}

        {showLabel && mediaLabel && (
          <MediaLabel
            variant={mediaLabel.variant}
            headline={mediaLabel.headline}
            variantsCount={mediaLabel.variantsCount}
            onVariantClick={onVariantClick}
          />
        )}

        {!selectiveMode && expandButton}
      </div>
    
      <MediaCardContent
        item={item}
        isSelectiveMode={selectiveMode}
        actionMenuItems={actionMenuItems}
        onRemove={onRemove}
      />

      {isDimmed && (
        <button
          type="button"
          className="media-list-item__focus-overlay"
          onClick={(event) => {
            event.stopPropagation()
            onDimmedClick?.(item)
          }}
          aria-label="Dismiss highlighted group"
        />
      )}

      {(selectiveMode || disableSelection) && (
        <div
          className={classnames('selective-mode-overlay', {
            'selective-mode-overlay--selected': isSelected,
            'selective-mode-overlay--disabled': disableSelection,
          })}
          onClick={selectiveMode && !disableSelection ? handleToggleSelection : undefined}
        >
          {selectiveMode && !disableSelection && (
            <div
              className="selective-mode-overlay__checkbox"
              onClick={(event) => event.stopPropagation()}
            >
              <CustomCheckBox
                checked={isSelected}
                onChange={handleToggleSelection}
              />
            </div>
          )}

          {selectiveMode && !disableSelection && expandButton}
        </div>
      )}
    </div>
  )
}

MediaCard.propTypes = {
  item: PropTypes.object.isRequired,
  selectiveMode: PropTypes.bool,
  selectedMedia: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setSelectedMedia: PropTypes.func,
  onPreviewClick: PropTypes.func,
  onExpandClick: PropTypes.func,
  showLabel: PropTypes.bool,
  mediaLabel: PropTypes.shape({
    variant: PropTypes.oneOf(['original', 'original-variants', 'variant-headline', 'variant']).isRequired,
    headline: PropTypes.string,
    variantsCount: PropTypes.number,
  }),
  onVariantClick: PropTypes.func,
  isGroupHighlighted: PropTypes.bool,
  isDimmed: PropTypes.bool,
  onDimmedClick: PropTypes.func,
  onRemove: PropTypes.func,
  onClick: PropTypes.func,
  disableSelection: PropTypes.bool,
  actionMenuItems: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  })),
}
